import React, { useEffect, useState } from 'react';
import { BASE_PATH } from '../utils/basePath';
import squareElement from '../assets/square_element.png';
import LoanCard from '../components/LoanCard';
import { Box, Container, Typography, useTheme, styled, Pagination, PaginationItem, Tooltip } from '@mui/material';
import Grid from '@mui/material/Grid';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { formatDateToDDMMYYYY } from '../utils/dateUtils';


const SectionHeader = styled(Typography)(({ theme }) => ({
  fontFamily: "'Libre Franklin', sans-serif",
  fontWeight: 400, // Book
  fontSize: 18,
  color: theme.palette.mode === 'dark' ? '#fff' : '#fff',
  margin: theme.spacing(4, 0, 2, 0),
}));

export interface Loan {
  id: string;
  name: string;
  loanAgreementNumber?: string;
  productName?: string;
  loanProductName?: string;
  status?: string;
  restrictAccessToPortal?: boolean;
  maturityDate?: string;
  numberOfInstallments?: string;
  accountName?: string;
  accountId?: string;
  loanAmount?: string;
  nextInstallmentDate?: string;
  amountDueTillCurrent?: string;
  payOffAmountAsOfToday?: string;
  interestRate?: string;
  rateType?: string;
  availableAmountForNextFunding?: string;
  disbursedAmount?: string;
  creditLimit?: string;
  disbursalAmount?: string;
  frequencyOfLoanPayment?: string;
  principalRemaining?: string;
  interestRemaining?: string;
  feesRemaining?: string;
  amountToCurrent?: string;
  schedule?: unknown[];
  totalOutstandingAsOfDate?: string;
  nextPayment?: string;
  currentSystemDate?: string;
  maxPayoffDate?: string;
}

const LoansDashboard: React.FC = () => {
  const theme = useTheme();
  const [activeLoans, setActiveLoans] = useState<Loan[]>([]);
  const [closedLoans, setClosedLoans] = useState<Loan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showActive, setShowActive] = useState(true);
  const [showNonActive, setShowNonActive] = useState(false);
  const [activeLoansPage, setActiveLoansPage] = useState(1);
  const [nonActiveLoansPage, setNonActiveLoansPage] = useState(1);
  const loansPerPage = 10;

  useEffect(() => {
    const fetchLoans = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(`${BASE_PATH}/api/loans`, { credentials: 'include' });
        
        if (res.status === 401) {
          window.location.href = `${BASE_PATH}/login`;
          return;
        }
        
        if (!res.ok) {
          throw new Error(`Failed to fetch loans: ${res.status} ${res.statusText}`);
        }
        
        const data = await res.json();
        setActiveLoans(Array.isArray(data.activeRecords) ? data.activeRecords : []);
        setClosedLoans(Array.isArray(data.closedRecords) ? data.closedRecords : []);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch loans';
        setError(errorMessage);
      }
      setLoading(false);
    };
    fetchLoans();
  }, []);

  // Summary: active contracts only
  const nonActiveLoans = closedLoans;
  const totalLoanAmount = activeLoans.reduce((sum, l) => sum + (parseFloat(l.loanAmount || '0')), 0);
  const today = formatDateToDDMMYYYY(new Date());

  // Pagination calculations
  const activeLoansTotalPages = Math.ceil(activeLoans.length / loansPerPage);
  const nonActiveLoansTotalPages = Math.ceil(nonActiveLoans.length / loansPerPage);
  
  const activeLoansStartIndex = (activeLoansPage - 1) * loansPerPage;
  const activeLoansEndIndex = activeLoansStartIndex + loansPerPage;
  const paginatedActiveLoans = activeLoans.slice(activeLoansStartIndex, activeLoansEndIndex);
  
  const nonActiveLoansStartIndex = (nonActiveLoansPage - 1) * loansPerPage;
  const nonActiveLoansEndIndex = nonActiveLoansStartIndex + loansPerPage;
  const paginatedNonActiveLoans = nonActiveLoans.slice(nonActiveLoansStartIndex, nonActiveLoansEndIndex);
  
  if (loading) return <Box textAlign="center" mt={8}><Typography color="#fff">Loading loans...</Typography></Box>;
  if (error) return <Box textAlign="center" mt={8}><Typography color="error.main">{error}</Typography></Box>;

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'center', background: 'transparent' }}>
        <Container maxWidth={false} sx={{ width: '80vw', maxWidth: '1200px', margin: '0 auto' }}>
          <Grid container spacing={4} alignItems="center" justifyContent="space-between">
            <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <Box display="flex" flexDirection="column" justifyContent="center" sx={{ minHeight: 180, py: 4, width: '100%' }}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Typography variant="subtitle1" sx={{ color: theme.palette.mode === 'dark' ? '#bdbdbd' : '#FFF', fontFamily: "'Libre Franklin', sans-serif", fontWeight: 500, fontSize: 16 }}>
                    Total loan amount
                  </Typography>
                  <Tooltip title="Total amount of all active loans">
                    <InfoOutlinedIcon fontSize="small" sx={{ color: '#bdbdbd' }} />
                  </Tooltip>
                </Box>
                <Typography variant="h2" sx={{ color: theme.palette.mode === 'dark' ? '#fff' : '#FFF', fontFamily: "'Libre Franklin', sans-serif", fontWeight: 500, fontSize: 56, mb: 2, userSelect: 'none', background: 'none', textAlign: 'left' }}>
                  £{totalLoanAmount.toLocaleString('en-GB', {minimumFractionDigits:2, maximumFractionDigits:2})}
                </Typography>
                <Typography variant="subtitle1" sx={{ color: theme.palette.mode === 'dark' ? '#bdbdbd' : '#FFF', fontFamily: "'Libre Franklin', sans-serif", fontWeight: 500, fontSize: 16, textAlign: 'left' }}>
                  No. of Active Loan: {activeLoans.length} &nbsp; | &nbsp; As of Date: <b style={{ color: theme.palette.mode === 'dark' ? '#fff' : '#FFF' }}>{today}</b>
                </Typography>
              </Box>
            </Grid>
            {/* <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              <Box display="flex" justifyContent="flex-end" alignItems="center" height={340} width="100%">
                <img src={squareElement} alt="artwork" style={{objectFit: 'contain', filter: 'drop-shadow(0 0 40px #98773b88)' }} />
              </Box>
            </Grid> */}
          </Grid>
          <Box mt={6}>
            <SectionHeader display="flex" alignItems="center" sx={{ cursor: 'pointer' }} onClick={() => {
              setShowActive(v => !v);
              if (!showActive) setActiveLoansPage(1); // Reset to first page when expanding
            }}>
              Active Loans
              {showActive ? <ExpandLessIcon sx={{ ml: 1 }} /> : <ExpandMoreIcon sx={{ ml: 1 }} />}
            </SectionHeader>
            {showActive && (activeLoans.length === 0 ? (
              <Typography variant="body1" sx={{ color: "#fff" }}>
                No active loans.
              </Typography>
            ) : (
              <>
                {paginatedActiveLoans.map((loan, idx) => <LoanCard key={loan.id || idx} loan={loan} />)}
                {activeLoans.length > loansPerPage && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, '& .MuiPaginationItem-root': { borderColor: 'rgba(255,255,255,0.5)' } }}>
                    <Pagination
                      count={activeLoansTotalPages}
                      page={activeLoansPage}
                      onChange={(_, page) => setActiveLoansPage(page)}
                      color="primary"
                      size="large"
                      shape="rounded"
                      showFirstButton
                      showLastButton
                      renderItem={(item) => (
                        <PaginationItem
                          {...item}
                          sx={{
                            borderRadius: '8px',
                            border: item.selected ? '1px solid #98773b' : '1px solid rgba(255,255,255,0.5)',
                            bgcolor: item.selected ? '#98773b !important' : '#fff',
                            color: item.selected ? '#fff' : '#333',
                            mx: 0.5,
                            minWidth: 36,
                            minHeight: 36,
                            fontWeight: 500,
                            fontSize: 16,
                            '& .MuiSvgIcon-root': { color: ['first', 'previous', 'next', 'last'].includes(item.type) ? (item.disabled ? 'rgba(0,0,0,0.26)' : '#333') : undefined },
                            '&:hover': {
                              color: '#fff',
                              '& .MuiSvgIcon-root': { color: '#fff' },
                            },
                          }}
                        />
                      )}
                    />
                  </Box>
                )}
              </>
            ))}
            <SectionHeader display="flex" alignItems="center" sx={{ mt: 5, cursor: 'pointer' }} onClick={() => {
              setShowNonActive(v => !v);
              if (!showNonActive) setNonActiveLoansPage(1); // Reset to first page when expanding
            }}>
              Non-Active Loans
              {showNonActive ? <ExpandLessIcon sx={{ ml: 1 }} /> : <ExpandMoreIcon sx={{ ml: 1 }} />}
            </SectionHeader>
            {showNonActive && (nonActiveLoans.length === 0 ? (
              <Typography variant="body1" sx={{ color: "#fff" }}>
                No non-active loans.
              </Typography>
            ) : (
              <>
                {paginatedNonActiveLoans.map((loan, idx) => <LoanCard key={loan.id || idx} loan={loan} />)}
                {nonActiveLoans.length > loansPerPage && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, '& .MuiPaginationItem-root': { borderColor: 'rgba(255,255,255,0.5)' } }}>
                    <Pagination
                      count={nonActiveLoansTotalPages}
                      page={nonActiveLoansPage}
                      onChange={(_, page) => setNonActiveLoansPage(page)}
                      color="primary"
                      size="large"
                      shape="rounded"
                      showFirstButton
                      showLastButton
                      renderItem={(item) => (
                        <PaginationItem
                          {...item}
                          sx={{
                            borderRadius: '8px',
                            border: item.selected ? '1px solid #98773b' : '1px solid rgba(255,255,255,0.5)',
                            bgcolor: item.selected ? '#98773b !important' : '#fff',
                            color: item.selected ? '#fff' : '#333',
                            mx: 0.5,
                            minWidth: 36,
                            minHeight: 36,
                            fontWeight: 500,
                            fontSize: 16,
                            '& .MuiSvgIcon-root': { color: ['first', 'previous', 'next', 'last'].includes(item.type) ? (item.disabled ? 'rgba(0,0,0,0.26)' : '#333') : undefined },
                            '&:hover': {
                              color: '#fff',
                              '& .MuiSvgIcon-root': { color: '#fff' },
                            },
                          }}
                        />
                      )}
                    />
                  </Box>
                )}
              </>
            ))}
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default LoansDashboard; 
